import * as React from 'react';

import { ShouldPreventDefaultForClick } from '@/components/LaunchStrategiesProvider/utils';
import {
  BannerTileProps,
  GameClickProps,
  GameTileProps,
} from '@/components/modular-grid/types';
import { CategoryFinancialWebAttributes } from '@/types/category/categoryTypes';

import styles from './Banner.module.scss';
import BannerComponent from './BannerComponent';

interface BannerProps {
  banner?: BannerTileProps;
  onClick?: (args: { gamePosition: number; game?: GameClickProps }) => void;
  onInfoClick?: (args: {
    gamePosition: number;
    game?: GameClickProps;
    path: string;
  }) => void;
  onRouteClick?: (args: { path?: string; isBanner?: boolean }) => void;
  onLinkClick?: (args: { url?: string; isBanner?: boolean }) => void;
  insideHeader?: boolean;
  gameDetailsInfo?: boolean;
  gameInfoTooltip?: string;
  slidesAmount?: number;
  cloned?: boolean;
  hidden?: boolean;
  categoryAttributes?: CategoryFinancialWebAttributes;
  index?: number;
}

const Banner: React.FC<BannerProps> = ({
  banner,
  onClick,
  onInfoClick,
  onRouteClick,
  onLinkClick,
  gameDetailsInfo,
  gameInfoTooltip,
  slidesAmount,
  cloned,
  categoryAttributes,
  index,
}) => {
  if (!banner) {
    return null;
  }

  const preventDefaultForGameClick =
    banner.game && ShouldPreventDefaultForClick(banner.game.integration);

  const handleOnClick = (
    banner: BannerTileProps,
    event?: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>
  ) => {
    const { game, link } = banner;
    if (game) {
      if (event && preventDefaultForGameClick) event.preventDefault();
      onClick && onClick({ gamePosition: 1, game });
    } else if (link) {
      onLinkClick && onLinkClick({ url: link.url, isBanner: true });
    }
  };

  const handleOnInfoClick = (game: GameTileProps, event?: React.MouseEvent) => {
    event && event.stopPropagation();
    onInfoClick &&
      onInfoClick({
        gamePosition: 1,
        game,
        path: game.gameDetailsPath,
      });
  };

  const {
    asset,
    smallLargeAsset,
    mediumAsset,
    game,
    link,
    type,
    route_link,
    notIndexed,
    buttonText,
    description,
    buttonPosition,
    personalization,
  } = banner;

  const isExternalUrl = type === 'External Link';

  const info = game
    ? {
        visible: game && gameDetailsInfo,
        onClick: (e: React.MouseEvent) => handleOnInfoClick(game, e),
        gameInfoTooltip,
      }
    : {};

  let gameLink;

  if (game && game.launchUrl) {
    gameLink = {
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) =>
        handleOnClick && handleOnClick(banner, e),
      url: game.launchUrl,
      target: '_blank',
      integration: game.integration,
      notIndexed,
    };
  }

  if (isExternalUrl && link) {
    gameLink = {
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) =>
        handleOnClick && handleOnClick(banner, e),
      target: link.target,
      url: link.url,
      shouldMapUrl: false,
    };
  }

  if (route_link) {
    gameLink = {
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onRouteClick &&
          onRouteClick({
            path: route_link,
            isBanner: true,
          });
      },
      url: route_link,
      target: '_self',
      shouldMapUrl: false,
      notIndexed,
    };
  }

  return (
    <div className={styles.bannerWrapper}>
      <BannerComponent
        asset={asset}
        smallLargeAsset={smallLargeAsset}
        mediumAsset={mediumAsset}
        gameLink={gameLink}
        bannerButton={buttonText}
        bannerDescription={description}
        gameBannerTitle={game?.title}
        isSingle={slidesAmount === 1}
        personalization={personalization}
        cloned={cloned}
        categoryAttributes={categoryAttributes}
        index={index}
        buttonPosition={buttonPosition}
        info={info}
        infoIcon={false}
        integration={game?.integration}
      />
    </div>
  );
};

export default Banner;
